import { Tabs as AntTabs, TabsProps as AntTabsProps, Flex, theme } from 'antd';
import styled, { css } from 'styled-components';
import type { GlobalToken } from 'antd';
import { Badge } from '../Badge';
import { Spinner } from '../Spinner';
import { ScrollableRow } from '../ScrollableRow';
import { getScrollArrowStyles } from './scrollArrowStyles';
import { radius } from '../../theme/radius';

/** Unread-dot color, called out explicitly in the design (not a design token). */
const UNREAD_BADGE_COLOR = '#22C55E';

export type TabsVariant = 'line' | 'card' | 'pill';
export type TabsSize = 'small' | 'medium' | 'large' | 'compact';
export type TabsIconPosition = 'left' | 'top';

/**
 * Status indicator shown next to a tab's label: `Loading` while its content
 * is still generating, `Unread` once it's ready but hasn't been opened yet.
 */
export const TabBadgeStatus = {
  Loading: 'loading',
  Unread: 'unread',
} as const;
export type TabBadgeStatus =
  (typeof TabBadgeStatus)[keyof typeof TabBadgeStatus];

export interface TabItem {
  /** Unique key for the tab */
  key: string;
  /** Tab label */
  label: React.ReactNode;
  /** Tab content */
  children?: React.ReactNode;
  /** Whether tab is disabled */
  disabled?: boolean;
  /** Icon for the tab */
  icon?: React.ReactNode;
  /** Status indicator shown next to the label (see `TabBadgeStatus`). */
  badgeStatus?: TabBadgeStatus;
}

export interface TabsProps extends Omit<
  AntTabsProps,
  'type' | 'items' | 'size'
> {
  /** Visual variant of tabs */
  variant?: TabsVariant;
  /** Tab items */
  items: TabItem[];
  /** Active tab key */
  activeKey?: string;
  /** Default active tab key */
  defaultActiveKey?: string;
  /** Callback when tab changes */
  onChange?: (key: string) => void;
  /** Whether tabs should take full width */
  fullWidth?: boolean;
  /** Size of the tabs */
  size?: TabsSize;
  /**
   * Where the icon sits relative to the label. `left` (default) keeps
   * the historical row layout. `top` stacks the icon above the label
   * (used by the Knowledge Hub panel's add-on tab strip).
   */
  iconPosition?: TabsIconPosition;
  /**
   * Replace AntD's overflow dropdown with a natively scrolling strip plus
   * edge fades and arrow buttons. Use it when the tab labels carry meaning
   * that a "more" menu would bury (e.g. the Knowledge Hub add-on strip).
   */
  scrollArrows?: boolean;
}

const getSizeConfig = (token: GlobalToken, size: TabsSize) => {
  const config = {
    small: {
      fontSize: token.fontSizeSM,
      padding: `${token.paddingXS}px ${token.paddingSM}px`,
      gap: `${token.marginXXS}px`,
    },
    medium: {
      fontSize: token.fontSize,
      padding: `${token.paddingSM}px ${token.padding}px`,
      gap: `${token.marginXS}px`,
    },
    large: {
      fontSize: token.fontSizeLG,
      padding: `${token.paddingSM}px ${token.paddingLG}px`,
      gap: `${token.marginXS}px`,
    },
    /** Tight strip; padding is off the 4px grid by design. */
    compact: {
      fontSize: token.fontSize,
      padding: '14px 18px',
      gap: `${token.marginXXS}px`,
    },
  };
  return config[size];
};

interface StyledTabsProps {
  $token: GlobalToken;
  $variant: TabsVariant;
  $size: TabsSize;
  $fullWidth: boolean;
  $iconPosition: TabsIconPosition;
  $scrollArrows: boolean;
}

const getLineStyles = (token: GlobalToken, size: TabsSize) => css`
  .ant-tabs-nav {
    &::before {
      border-bottom: ${token.lineWidth}px solid ${token.colorBorderSecondary};
    }
  }

  .ant-tabs-tab {
    padding: ${getSizeConfig(token, size).padding};
    font-size: ${getSizeConfig(token, size).fontSize}px;
    color: ${token.colorTextSecondary};
    transition: all ${token.motionDurationMid} ease;
    ${size === 'compact' &&
    css`
      letter-spacing: -0.02em;
    `}

    /* Inherit color into the icon SVG so non-active tabs render the
       icon + text in the same tertiary tone. */
    .ant-tabs-tab-btn svg {
      color: inherit;
    }

    &:hover {
      color: ${token.colorPrimary};
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: ${token.colorPrimary};
        /* Compact marks the active tab by color only, not bold. */
        font-weight: ${size === 'compact' ? 400 : token.fontWeightStrong};
      }
    }

    ${size === 'compact' &&
    css`
      /* Drop AntD's ~32px gutter; tab padding already gives the hit area. */
      + .ant-tabs-tab {
        margin: 0;
      }
    `}
  }

  .ant-tabs-ink-bar {
    background: ${token.colorPrimary};
    height: ${token.lineWidthBold}px;
  }

  ${size === 'compact' &&
  css`
    /* Hairline for compact; &&& beats AntD's 3-class .ant-tabs-top > .ant-tabs-nav .ant-tabs-ink-bar. */
    &&& .ant-tabs-ink-bar {
      height: ${token.lineWidth}px;
    }
  `}
`;

const getCardStyles = (token: GlobalToken, size: TabsSize) => css`
  .ant-tabs-nav {
    &::before {
      border-bottom: none;
    }
  }

  .ant-tabs-tab {
    padding: ${getSizeConfig(token, size).padding};
    font-size: ${getSizeConfig(token, size).fontSize}px;
    color: ${token.colorTextSecondary};
    background: ${token.colorBgLayout};
    border: ${token.lineWidth}px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadius}px ${token.borderRadius}px 0 0;
    margin-right: ${token.marginXXS}px;
    transition: all ${token.motionDurationMid} ease;

    &:hover {
      color: ${token.colorPrimary};
    }

    &.ant-tabs-tab-active {
      background: ${token.colorBgContainer};
      border-bottom-color: ${token.colorBgContainer};

      .ant-tabs-tab-btn {
        color: ${token.colorPrimary};
        font-weight: ${token.fontWeightStrong};
      }
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-content-holder {
    border: ${token.lineWidth}px solid ${token.colorBorderSecondary};
    border-top: none;
    border-radius: 0 0 ${radius.box}px ${radius.box}px;
    padding: ${token.padding}px;
    background: ${token.colorBgContainer};
  }
`;

const getPillStyles = (token: GlobalToken, size: TabsSize) => css`
  .ant-tabs-nav {
    &::before {
      border-bottom: none;
    }

    .ant-tabs-nav-list {
      background: ${token.colorBgLayout};
      border-radius: ${token.borderRadius}px;
      padding: ${token.paddingXXS}px;
    }
  }

  .ant-tabs-tab {
    padding: ${getSizeConfig(token, size).padding};
    font-size: ${getSizeConfig(token, size).fontSize}px;
    color: ${token.colorTextTertiary};
    border-radius: ${token.borderRadius - 2}px;
    margin: 0;
    transition: all ${token.motionDurationMid} ease;

    &:hover {
      color: ${token.colorText};
    }

    &.ant-tabs-tab-active {
      background: ${token.colorBgContainer};
      box-shadow: ${token.boxShadowTertiary};

      .ant-tabs-tab-btn {
        color: ${token.colorPrimary};
        font-weight: ${token.fontWeightStrong};
      }
    }

    + .ant-tabs-tab {
      margin-left: ${token.marginXXS}px;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }
`;

const StyledTabs = styled(AntTabs)<StyledTabsProps>`
  font-family: ${({ $token }) => $token.fontFamily};

  .ant-tabs-tab {
    font-family: ${({ $token }) => $token.fontFamily};

    .ant-tabs-tab-btn {
      display: flex;
      flex-direction: ${({ $iconPosition }) =>
        $iconPosition === 'top' ? 'column' : 'row'};
      align-items: center;
      gap: ${({ $token, $size, $iconPosition }) =>
        $iconPosition === 'top'
          ? `${$token.marginXXS}px`
          : getSizeConfig($token, $size).gap};
    }

    &.ant-tabs-tab-disabled {
      color: ${({ $token }) => $token.colorTextDisabled};
      cursor: not-allowed;
    }
  }

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      .ant-tabs-nav-list {
        width: 100%;
      }

      .ant-tabs-tab {
        flex: 1;
        justify-content: center;
      }
    `}

  ${({ $scrollArrows }) => $scrollArrows && getScrollArrowStyles()}

  ${({ $token, $variant, $size }) => {
    switch ($variant) {
      case 'card':
        return getCardStyles($token, $size);
      case 'pill':
        return getPillStyles($token, $size);
      default:
        return getLineStyles($token, $size);
    }
  }}
`;

/** Matches the Spinner's `size="xs"` box so the slot below never resizes. */
const BADGE_SLOT_SIZE = 16;

const StyledTabBadgeSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${BADGE_SLOT_SIZE}px;
  height: ${BADGE_SLOT_SIZE}px;
  flex-shrink: 0;
`;

/**
 * Tabs component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Tabs } from '@libertify/ui';
 *
 * const items = [
 *   { key: '1', label: 'Tab 1', children: <div>Content 1</div> },
 *   { key: '2', label: 'Tab 2', children: <div>Content 2</div> },
 *   { key: '3', label: 'Tab 3', children: <div>Content 3</div> },
 * ];
 *
 * <Tabs items={items} variant="line" />
 * <Tabs items={items} variant="pill" fullWidth />
 * ```
 */
export const Tabs = ({
  variant = 'line',
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  fullWidth = false,
  size = 'medium',
  iconPosition = 'left',
  scrollArrows = false,
  renderTabBar,
  ...props
}: TabsProps) => {
  const { token } = theme.useToken();

  // Convert our TabItem to Ant Design format
  const antItems = items.map((item) => ({
    key: item.key,
    label: (
      <Flex align="center" gap={getSizeConfig(token, size).gap}>
        {item.icon}
        {item.label}
        {item.badgeStatus === TabBadgeStatus.Loading && (
          <StyledTabBadgeSlot data-testid="tab-badge-loading">
            <Spinner size="xs" />
          </StyledTabBadgeSlot>
        )}
        {item.badgeStatus === TabBadgeStatus.Unread && (
          <StyledTabBadgeSlot data-testid="tab-badge-unread">
            <Badge dot status="success" animated color={UNREAD_BADGE_COLOR} />
          </StyledTabBadgeSlot>
        )}
      </Flex>
    ),
    children: item.children,
    disabled: item.disabled,
  }));

  /** Wraps the tab bar — the consumer's, if they supplied one — in the arrow overlay. */
  const renderBarWithArrows: AntTabsProps['renderTabBar'] = (
    barProps,
    DefaultTabBar,
  ) => (
    <StyledScrollableTabBar
      $token={token}
      activeKey={barProps.activeKey}
      activeSelector=".ant-tabs-tab-active"
      scrollSelector=".ant-tabs-nav"
      contentSelector=".ant-tabs-nav-list"
      bottomInset={token.lineWidth}
    >
      {renderTabBar ? (
        renderTabBar(barProps, DefaultTabBar)
      ) : (
        <DefaultTabBar {...barProps} />
      )}
    </StyledScrollableTabBar>
  );

  return (
    <StyledTabs
      $token={token}
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $iconPosition={iconPosition}
      $scrollArrows={scrollArrows}
      items={antItems}
      activeKey={activeKey}
      defaultActiveKey={defaultActiveKey}
      onChange={onChange}
      renderTabBar={scrollArrows ? renderBarWithArrows : renderTabBar}
      {...props}
    />
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

/**
 * AntD's bottom margin sits on the nav itself, which would drop the fades and
 * arrows below the tab row, so it moves up to the wrapper instead.
 */
const StyledScrollableTabBar = styled(ScrollableRow)<{ $token: GlobalToken }>`
  margin-bottom: ${({ $token }) => $token.margin}px;

  && > .ant-tabs-nav {
    margin-bottom: 0;
  }
`;

export default Tabs;
